import { ComponentUtil, Overlay, ScreenStack, SlideoutDecorator } from "@mcleod/components";
import { VirtualKeyboardAccessor } from "./VirtualKeyboardAccessor";
import { KeyboardInfo } from "./VirtualKeyboardDef";
import { Alignment, DeviceAccessor, DOMUtil, LogManager } from "@mcleod/core";
import { MobileDeviceAccessor } from "../device";

const log = LogManager.getLogger("common.VirtualKeyboardDisplayHandler");

const LIFT_TRANSITION = "bottom 200ms ease-out, max-height 200ms ease-out";
/** How long after keyboardWillShow to wait for the native side to finish any viewport resize before measuring. */
const SETTLE_DELAY_MS = 60;
/** A second look after the first adjustment, for content that reflows once the viewport has finished changing. */
const FOLLOW_UP_DELAY_MS = 250;
/** How long after focus moves to another field (keyboard already up) before we check that field is in view. */
const FOCUS_CHANGE_DELAY_MS = 120;
/** Breathing room kept between a focused field and the edge of the visible area. */
const REVEAL_MARGIN_PX = 12;

/**
 * Keeps the focused field visible while the on-screen keyboard is showing.
 *
 * How much work is needed depends on the platform and Capacitor version: iOS with `Keyboard.resize = none` leaves
 * the WebView full height, while Capacitor 8.5+ on Android pads the window by the keyboard height itself (through
 * the SystemBars insets handling), so the WebView has already shrunk by the time our listener runs.  Rather than
 * assume either, this handler measures how much the viewport actually shrank and compensates only for the part of
 * the keyboard that still overlaps the page:
 *   - a bottom sheet (SlideoutDecorator sliding in from the bottom) is lifted by the overlap so it sits on top of
 *     the keyboard like a native sheet, and capped so it cannot grow taller than the space that is left;
 *   - any other screen / overlay / layout has its height reduced by the overlap.
 * When the overlap is zero (the platform already resized the WebView) nothing is moved, which is what stops the
 * "accounting for the keyboard twice" gap.
 *
 * Independently of that, the focused field is scrolled into the part of the viewport the keyboard leaves free.
 * Browsers only do this on their own when focus is given (before the keyboard exists) or when the caret moves while
 * typing, so a field low on the page can sit under the keyboard until the first keystroke.  The check runs after
 * each keyboard event, again once layout has settled, whenever the viewport resizes while the keyboard is up (the
 * native shrink can land after the keyboard events), and whenever focus moves to another field while the keyboard
 * is already showing (no keyboard event fires for that).  It measures against the keyboard-free viewport, not
 * against whichever container happens to hold the field, and scrolls the nearest scrollable ancestors itself.
 */
export class VirtualKeyboardDisplayHandler {
    private static _instance: VirtualKeyboardDisplayHandler;

    private resizeElement: HTMLElement | null;
    private originalResizePanelHeight: number | null = null;
    private scrollLocked: boolean = false;

    private liftedElement: HTMLElement | null = null;
    private liftedElementOriginalStyle: { bottom: string; maxHeight: string; height: string; transition: string; paddingBottom: string } | null = null;

    /** Viewport height measured while no keyboard was showing; the baseline for detecting a native resize. */
    private viewportHeightWithoutKeyboard: number = window.innerHeight;
    private keyboardShowing = false;
    /** Height reported by the most recent keyboard event, so later re-checks (resize, focus change) can reuse it. */
    private lastKeyboardHeight = 0;
    private pendingApply: ReturnType<typeof setTimeout> | null = null;
    private pendingFollowUp: ReturnType<typeof setTimeout> | null = null;

    private constructor() {
        if (DeviceAccessor.isAppTestingViaWeb()) {
            return;
        }
        const accessor = VirtualKeyboardAccessor.instance;
        accessor.addWillShowListener(this.willShowWork);
        accessor.addDidShowListener(this.didShowWork);
        accessor.addWillHideListener(this.willHideWork);
        accessor.addDidHideListener(this.didHideWork);
        // A platform that shrinks the WebView for the keyboard does so *before* keyboardWillShow arrives, so a plain
        // resize listener would record the shrunken height as the no-keyboard baseline and defeat the measurement.
        // Growth can never be caused by the keyboard, so only growth updates the baseline here; shrinks are picked up
        // once we positively know the keyboard is gone (didHide) or the device rotated.
        window.addEventListener("resize", () => {
            if (!this.keyboardShowing && window.innerHeight > this.viewportHeightWithoutKeyboard)
                this.viewportHeightWithoutKeyboard = window.innerHeight;
            // The platform's own resize for the keyboard can arrive after keyboardDidShow; re-measure when it does.
            if (this.keyboardShowing)
                this.schedule(() => this.applyKeyboardAdjustment(this.lastKeyboardHeight), SETTLE_DELAY_MS);
        });
        window.addEventListener("orientationchange", () => this.rebaselineWhenIdle());
        // Moving from one field to the next while the keyboard is up produces no keyboard event at all.
        document.addEventListener("focusin", (event) => {
            if (this.keyboardShowing && VirtualKeyboardDisplayHandler.isEditable(event.target as Element))
                this.schedule(() => this.applyKeyboardAdjustment(this.lastKeyboardHeight), FOCUS_CHANGE_DELAY_MS);
        }, true);
    }

    /** Re-reads the no-keyboard viewport height once layout has settled after the keyboard hid or the device rotated. */
    private rebaselineWhenIdle(): void {
        window.setTimeout(() => {
            if (!this.keyboardShowing)
                this.viewportHeightWithoutKeyboard = window.innerHeight;
        }, 300);
    }

    private didHideWork = () => {
        this.rebaselineWhenIdle();
    };

    /**
     * Prevents scrolling past the resized element into blank space on Android.
     * iOS handles scroll containment natively, so this is only needed for Android.
     */
    private lockBodyScroll(): void {
        if (this.scrollLocked || MobileDeviceAccessor.instance.platform !== "android") return;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        this.scrollLocked = true;
    }

    private unlockBodyScroll(): void {
        if (!this.scrollLocked) return;
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
        this.scrollLocked = false;
    }

    private willShowWork = (info: KeyboardInfo) => {
        log.debug("Performing keyboardWillShow work");
        if (!this.keyboardShowing) {
            this.keyboardShowing = true;
            this.viewportHeightWithoutKeyboard = Math.max(this.viewportHeightWithoutKeyboard, window.innerHeight);
        }
        this.lastKeyboardHeight = info.keyboardHeight;
        // Give a platform that resizes the WebView itself a moment to do so before we measure.
        this.schedule(() => this.applyKeyboardAdjustment(info.keyboardHeight), SETTLE_DELAY_MS);
    };

    private didShowWork = (info: KeyboardInfo) => {
        this.lastKeyboardHeight = info.keyboardHeight;
        // Re-measure once the keyboard is fully up; the viewport may have finished resizing since willShow.
        this.schedule(() => this.applyKeyboardAdjustment(info.keyboardHeight), 0);
    };

    private schedule(work: () => void, delayMs: number): void {
        if (this.pendingApply != null)
            clearTimeout(this.pendingApply);
        this.pendingApply = setTimeout(() => {
            this.pendingApply = null;
            work();
        }, delayMs);
    }

    private clearScheduled(): void {
        if (this.pendingApply != null) {
            clearTimeout(this.pendingApply);
            this.pendingApply = null;
        }
        if (this.pendingFollowUp != null) {
            clearTimeout(this.pendingFollowUp);
            this.pendingFollowUp = null;
        }
    }

    /**
     * Portion of the keyboard, in CSS pixels, that still covers the page after any native resize.
     * Exposed for unit tests.
     */
    public static overlapFor(keyboardHeight: number, viewportHeightWithoutKeyboard: number, viewportHeightNow: number): number {
        const nativeShrink = Math.max(0, viewportHeightWithoutKeyboard - viewportHeightNow);
        return Math.max(0, Math.round(keyboardHeight - nativeShrink));
    }

    private applyKeyboardAdjustment(keyboardHeight: number): void {
        if (!this.keyboardShowing)
            return;
        const focused = document.activeElement as HTMLElement;
        if (!focused) return;

        const overlap = VirtualKeyboardDisplayHandler.overlapFor(
            keyboardHeight, this.viewportHeightWithoutKeyboard, window.innerHeight);
        log.debug(`Keyboard ${keyboardHeight}px, viewport ${this.viewportHeightWithoutKeyboard}px -> ${window.innerHeight}px, overlap ${overlap}px`);

        const bottomSheet = this.findBottomSheetContaining(focused);
        if (bottomSheet != null) {
            if (overlap > 0)
                this.liftBottomSheet(bottomSheet, overlap);
            else
                this.restoreBottomSheet();
            this.lockBodyScroll();
        } else {
            const currentResizeElement: HTMLElement = this.determineResizeComponent(focused);
            if (currentResizeElement) {
                if (overlap > 0) {
                    const panelHeight = this.originalResizePanelHeight ?? DOMUtil.getElementHeight(currentResizeElement);
                    this.originalResizePanelHeight ??= panelHeight;
                    this.resizeElement ??= currentResizeElement;

                    const newHeight = panelHeight - overlap;
                    log.debug(`Resizing container to ${newHeight}px while keyboard is displaying.`);
                    currentResizeElement.style.height = `${newHeight}px`;
                    currentResizeElement.style.overscrollBehavior = "contain";
                } else if (this.resizeElement != null) {
                    this.restoreResizedElement();
                }
                this.lockBodyScroll();
            }
        }

        this.revealFocusedField(overlap);
        if (this.pendingFollowUp != null)
            clearTimeout(this.pendingFollowUp);
        this.pendingFollowUp = setTimeout(() => {
            this.pendingFollowUp = null;
            if (this.keyboardShowing)
                this.revealFocusedField(VirtualKeyboardDisplayHandler.overlapFor(
                    this.lastKeyboardHeight, this.viewportHeightWithoutKeyboard, window.innerHeight));
        }, FOLLOW_UP_DELAY_MS);
    }

    /**
     * Scrolls whatever is needed so the focused field is actually on screen above the keyboard.
     *
     * Every scrollable ancestor gets a say, innermost first, and each judges the field against its *own* on-screen,
     * keyboard-free box rather than against the viewport.  That distinction is the whole point: a fill-height panel
     * above a pinned button bar shrinks when the keyboard appears, and a field just past its bottom edge is clipped
     * by that panel while still sitting above the keyboard line.  Checking the viewport alone calls such a field
     * visible and leaves it hidden behind the button bar.  Each container is moved by the smallest amount that brings
     * the field inside it (centred when there is room); the document itself is the last resort, which is what the
     * browser does when the caret moves while typing.
     */
    private revealFocusedField(overlap: number): void {
        const focused = document.activeElement as HTMLElement | null;
        if (!VirtualKeyboardDisplayHandler.isEditable(focused))
            return;
        const visibleBottom = window.innerHeight - Math.max(0, overlap);
        const margin = REVEAL_MARGIN_PX;
        // Prefer the whole textbox (caption included) when it fits comfortably, else just the input.
        const component = focused.closest(".txt-base") as HTMLElement | null;
        let target: HTMLElement = focused;
        if (component != null && component.getBoundingClientRect().height + 2 * margin <= visibleBottom * 0.6)
            target = component;

        let rect: { top: number; bottom: number } = target.getBoundingClientRect();
        for (let el = target.parentElement; el != null && el !== document.documentElement; el = el.parentElement) {
            if (!VirtualKeyboardDisplayHandler.isScrollContainer(el))
                continue;
            const box = el.getBoundingClientRect();
            const newTop = VirtualKeyboardDisplayHandler.scrollTopToReveal({
                targetTop: rect.top, targetBottom: rect.bottom,
                containerTop: Math.max(box.top, 0), containerBottom: Math.min(box.bottom, visibleBottom),
                scrollTop: el.scrollTop, maxScrollTop: el.scrollHeight - el.clientHeight, margin
            });
            const delta = newTop - el.scrollTop;
            if (Math.abs(delta) >= 1) {
                log.debug(`Scrolling [${el.id || el.className}] by ${delta}px to reveal the focused field`);
                el.scrollTo({ top: newTop, behavior: "smooth" });
                rect = { top: rect.top - delta, bottom: rect.bottom - delta };
            }
        }

        if (rect.top >= margin && rect.bottom <= visibleBottom - margin)
            return;
        const doc = document.scrollingElement ?? document.documentElement;
        const newTop = VirtualKeyboardDisplayHandler.scrollTopToReveal({
            targetTop: rect.top, targetBottom: rect.bottom, containerTop: 0, containerBottom: visibleBottom,
            scrollTop: doc.scrollTop, maxScrollTop: doc.scrollHeight - doc.clientHeight, margin
        });
        if (Math.abs(newTop - doc.scrollTop) >= 1) {
            log.debug(`Scrolling the document by ${newTop - doc.scrollTop}px to reveal the focused field`);
            window.scrollTo({ top: newTop, behavior: "smooth" });
        }
    }

    /**
     * The scrollTop that places a field (given in viewport coordinates) inside a scroll container's visible,
     * keyboard-free box: unchanged when it is already inside with `margin` to spare, otherwise centred when it fits
     * and aligned just under the top edge when it does not.  Clamped to what the container can actually scroll.
     * Pure, for unit tests.
     */
    public static scrollTopToReveal(args: {
        targetTop: number; targetBottom: number;
        containerTop: number; containerBottom: number;
        scrollTop: number; maxScrollTop: number; margin: number;
    }): number {
        const { targetTop, targetBottom, containerTop, containerBottom, scrollTop, maxScrollTop, margin } = args;
        const visibleHeight = containerBottom - containerTop;
        const targetHeight = targetBottom - targetTop;
        if (visibleHeight <= 0 || maxScrollTop <= 0)
            return scrollTop;
        const fits = targetHeight + 2 * margin <= visibleHeight;
        if (fits && targetTop >= containerTop + margin && targetBottom <= containerBottom - margin)
            return scrollTop;
        // Too tall to keep a margin but wholly inside anyway: leave it.
        if (!fits && targetTop >= containerTop && targetBottom <= containerBottom)
            return scrollTop;
        const desiredTop = fits ? containerTop + (visibleHeight - targetHeight) / 2 : containerTop + margin;
        const wanted = scrollTop + (targetTop - desiredTop);
        return Math.round(Math.max(0, Math.min(maxScrollTop, wanted)));
    }

    /** Text-entry elements that summon the keyboard. */
    public static isEditable(element: Element | null | undefined): element is HTMLElement {
        if (element == null || !(element instanceof HTMLElement))
            return false;
        if (element.isContentEditable)
            return true;
        if (element instanceof HTMLTextAreaElement)
            return true;
        if (element instanceof HTMLInputElement) {
            const type = (element.type || "text").toLowerCase();
            return !["button", "submit", "reset", "checkbox", "radio", "range", "color", "file", "hidden", "image"].includes(type);
        }
        return false;
    }

    private static isScrollContainer(element: HTMLElement): boolean {
        if (element.scrollHeight <= element.clientHeight + 1)
            return false;
        const overflowY = window.getComputedStyle(element).overflowY;
        return overflowY === "auto" || overflowY === "scroll";
    }

    private willHideWork = () => {
        log.debug("Performing keyboardHidden work");
        this.clearScheduled();
        this.keyboardShowing = false;
        this.restoreBottomSheet();
        this.restoreResizedElement();
        this.unlockBodyScroll();
        log.debug("Finished keyboardHidden work");
    };

    private restoreResizedElement(): void {
        if (this.resizeElement) {
            log.debug(`Reverting resize container [${this.resizeElement.id ?? "no-id"}] to original size.`);
            this.resizeElement.style.height = `${this.originalResizePanelHeight}px`;
            this.resizeElement.style.overscrollBehavior = "";
            this.originalResizePanelHeight = null;
            this.resizeElement = null;
        }
    }

    public static initialize() {
        if (this._instance == null) {
            this._instance = new VirtualKeyboardDisplayHandler();
        }
    }

    /**
     * Returns the element of the newest overlay's SlideoutDecorator when it slid in from the bottom and owns the
     * focused element; null otherwise.
     */
    private findBottomSheetContaining(focusedElement: HTMLElement): HTMLElement | null {
        const overlay = ScreenStack.getNewestOverlay();
        const component = overlay?.component;
        if (!(component instanceof SlideoutDecorator))
            return null;
        if (component.transitionOptions?.direction !== Alignment.BOTTOM)
            return null;
        if (!DOMUtil.isOrContains(component._element, focusedElement))
            return null;
        return component._element;
    }

    private liftBottomSheet(element: HTMLElement, overlap: number): void {
        if (this.liftedElement !== element) {
            this.restoreBottomSheet();
            this.liftedElement = element;
            this.liftedElementOriginalStyle = {
                bottom: element.style.bottom,
                maxHeight: element.style.maxHeight,
                height: element.style.height,
                transition: element.style.transition,
                paddingBottom: element.style.paddingBottom
            };
            // The home-indicator padding is pointless while the sheet floats above the keyboard.  The slide-in
            // transition pinned an explicit height that includes it, so shrink that height by the same amount or the
            // sheet keeps an empty band above the keyboard.
            const safeAreaPadding = parseFloat(window.getComputedStyle(element).paddingBottom) || 0;
            if (safeAreaPadding > 0 && element.style.height) {
                const current = parseFloat(element.style.height);
                if (!Number.isNaN(current))
                    element.style.height = `${Math.max(0, current - safeAreaPadding)}px`;
            }
            element.style.paddingBottom = "0px";
        }
        const available = Math.max(160, window.innerHeight - overlap);
        log.debug(`Lifting bottom sheet by ${overlap}px (max height ${available}px)`);
        element.style.transition = LIFT_TRANSITION;
        element.style.bottom = `${overlap}px`;
        element.style.maxHeight = `${available}px`;
        element.style.overflowY = "auto";
    }

    private restoreBottomSheet(): void {
        const element = this.liftedElement;
        if (element == null)
            return;
        const original = this.liftedElementOriginalStyle;
        element.style.transition = LIFT_TRANSITION;
        element.style.bottom = original?.bottom || "0px";
        element.style.maxHeight = original?.maxHeight ?? "";
        element.style.height = original?.height ?? "";
        element.style.overflowY = "";
        element.style.paddingBottom = original?.paddingBottom ?? "";
        window.setTimeout(() => {
            // only clear the transition if nothing else has taken the element over in the meantime
            if (element.style.transition === LIFT_TRANSITION)
                element.style.transition = original?.transition ?? "";
        }, 220);
        this.liftedElement = null;
        this.liftedElementOriginalStyle = null;
    }

    private determineResizeComponent(focusedElement: HTMLElement): HTMLElement {
        const currentScreen = ScreenStack.getCurrentScreen();
        log.debug(`Focused element = [${focusedElement.id}]`);

        if (currentScreen != null && DOMUtil.isOrContains(currentScreen._element, focusedElement)) {
            log.debug("Resizing current screen for virtual keyboard: %o", currentScreen);
            return currentScreen._element;
        }

        const currentOverlay = ScreenStack.getNewestOverlay();
        if (currentOverlay != null && DOMUtil.isOrContains(currentOverlay.getOverlayContent()._element, focusedElement)) {
            log.debug("Resizing current overlay content for virtual keyboard: %o", currentOverlay);
            return this.getOverlayResizeComponent(currentOverlay);
        }

        const rootLayout = ComponentUtil.getComponentForElement(focusedElement)?.getRootLayout()?._element;
        if (rootLayout)
            log.debug("Resizing root layout for virtual keyboard: %o", rootLayout);
        else
            log.debug("Unable to find htmlElement to resize for virtual keyboard.");
        return rootLayout;
    }

    private getOverlayResizeComponent(overlay: Overlay): HTMLElement {
        if (overlay.component instanceof SlideoutDecorator) {
            if (overlay.component.transitionOptions.direction === Alignment.BOTTOM)
                return null; // handled by liftBottomSheet()
        }
        return overlay.component._element;
    }
}
